'use client';

/**
 * ObeyaPlanLensFirmnessSelectedCard — every selected requirement with its quoted, allocated and RFQ marks (the known knowns).
 * A renderer: every count, mark and range arrives computed from the BFF.
 *
 * @see klyde_edge/ui_config/composition/cards/obeya.py — ObeyaPlanLensFirmnessSelectedCard
 * @see klyde_edge/klyde_intelligence/projects/plan_lenses.py
 *
 * @component_role: Card
 * @component_type: Card Renderer
 * @domain: Canvas / Obeya
 * @layer: presentation
 */

import { useMemo } from 'react';
import { text } from '@/lib/design/tokens';
import { cn } from '@/lib/utils/cn';
import { useCardExport } from '@/lib/design/card-export';
import { useCardIdentity } from '@/lib/design/card-chrome';
import { usePlanLenses } from './use-plan-lenses';

export function ObeyaPlanLensFirmnessSelectedCard() {
  const { contentKey } = useCardIdentity();
  const { mcpId, strategy, d, isLoading, queryKey } = usePlanLenses();

  useCardExport(useMemo(() => ({
    content_key: contentKey ?? 'obeya_plan_lens_firmness_selected',
    status: (d ? 'loaded' : mcpId && strategy ? 'loading' : 'idle') as 'loaded' | 'loading' | 'idle',
    query_key: queryKey,
    text: d ? d.labels.selected_requirements : 'obeya_plan_lens_firmness_selected',
    json: d ? d.knowledge : null,
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [contentKey, d, mcpId, strategy]));

  if (!mcpId) return null;
  if (isLoading && !d) return <div className={cn(text.muted, 'p-6 text-center')}>…</div>;
  if (!d) return null;

  const L = d.labels;
  const k = d.knowledge.data;
  const mark = (yes: boolean) => (yes ? L.mark_yes : L.mark_no);

  return (
    <div className="flex flex-col h-full p-6 gap-3 overflow-auto">
      <div>
        <div className={cn(text.eyebrow, 'mb-2')}>{L.selected_requirements} · {k.selected_count}</div>
        <div className={cn(text.muted, 'flex gap-3 pb-1 border-b border-border')}>
          <span className="w-[26%]">{L.col_requirement}</span>
          <span className="flex-1">{L.col_rooms}</span>
          <span className="w-[10%] text-center">{L.col_quoted}</span>
          <span className="w-[10%] text-center">{L.col_allocated}</span>
          <span className="w-[10%] text-center">{L.col_rfq}</span>
        </div>
        {k.selected.map((r) => (
          <div key={r.requirement_id} className={cn(text.body, 'flex gap-3 py-1 items-center')}>
            <span className="w-[26%] truncate">{r.requirement ?? L.unlabelled}</span>
            <span className={cn(text.muted, 'flex-1 truncate')}>{r.rooms.join(', ')}</span>
            <span className="w-[10%] text-center">{mark(r.quoted)}</span>
            <span className="w-[10%] text-center">{mark(r.fully_allocated)}</span>
            <span className="w-[10%] text-center">{mark(r.reached_sent_rfq)}</span>
          </div>
        ))}
      </div>

    </div>
  );
}
